import {
  FC, MutableRefObject, ReactNode, useCallback, useMemo, useRef, useState,
} from 'react';
import {
  ColumnsPanelTrigger,
  ExportCsv,
  ExportPrint,
  FilterPanelTrigger,
  GridApiPro,
  QuickFilter,
  QuickFilterClear,
  QuickFilterControl,
  QuickFilterTrigger,
  Toolbar,
  ToolbarButton,
} from '@mui/x-data-grid-pro';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid2';
import { Iconify } from '@mgm/common/components';
import { BASE_COLORS } from '@mgm/common/utils';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import {
  Menu, MenuItem, useMediaQuery, useTheme,
} from '@mui/material';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import CancelIcon from '@mui/icons-material/Cancel';
import { styled } from '@mui/system';
import TextField from '@mui/material/TextField';
import { Moment } from 'moment-timezone';
import Icon from '@mdi/react';
import { mdiChevronDown } from '@mdi/js';
import { BaseButtonMenu } from '@mgm/common/components/base';

import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { PickersActionBarProps } from '@mui/x-date-pickers/PickersActionBar';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { useTranslation } from 'react-i18next';
import { usePrint } from '../../hooks/usePrint';
import { TListBaseItem } from '../base/BList/type';
import { useAppDispatch, useAppSelector } from '../../hooks/useAppStore';
import { getTokenDetails } from '../../store/authenticate/selectors';
import { sheetsActions } from '../../store/sheets/slice';
import { getSelectedMembers } from '../../store/sheets/selectors';

type TTableHeader = {
  isDisplayCustomFilter?: boolean;
  customAction?: ReactNode;
  LoaderComponent?: ReactNode;
  customExportItems?: ReactNode;
  exportFileName?: string;
  printRef: MutableRefObject<HTMLDivElement | null>;
  filterItems?: Array<TListBaseItem>;
  apiRef: MutableRefObject<GridApiPro>;
  handleScrollTable?: (position: string) => void;
  isDisplayPicker?: boolean;
  pickerButtonText?: string;
  onPickerSubmit?: () => void;
  isPickerBtnDisable?: boolean;
  isPickerLoading?: boolean;
  chosenDates?: [Moment, Moment];
  handleSetDates?: (dates: [Moment, Moment]) => void;
  isSheets?: boolean;
  isOrders?: boolean;
  values?: Record<string, any>;
  setFullTable: (val: boolean) => void;
  isFullTable: boolean;
  handleExport?: any;
  pickerItems?: Array<TListBaseItem>;
  selectedPickerName?: string;
  isOpenCustomPicker?: boolean,
  isDisplaySelectedMembers?: boolean
  setIsOpenCustomPicker?: (val: boolean) => void,
};

type OwnerState = {
  expanded: boolean;
};

const CustomDateRangeFooter = (props: PickersActionBarProps & { selectedRange: [Moment | null, Moment | null] }) => {
  const { onAccept, onCancel, selectedRange } = props;
  const { t } = useTranslation(['common', 'dashboard', 'orders']);

  const periodText = useMemo(() => {
    const [start, end] = selectedRange;

    if (!start || !end) return t('pleaseSelectADateRange');
    if (start.year() === end.year()) {
      return `${start.format('MMM D')} - ${end.format('MMM D, YYYY')}`;
    }
    return `${start.format('MMM D, YYYY')} - ${end.format('MMM D, YYYY')}`;
  }, [selectedRange]);

  return (
    <Box
      position="fixed"
      bottom={0}
      left={0}
      right={0}
      bgcolor="background.paper"
      sx={{
        borderTop: '1px solid',
        borderColor: 'divider',
        px: 2,
        py: 3,
      }}
    >
      <Box py={4} position="fixed" bottom={-6} left={0} display="flex" gap={1} p={2} justifyContent="flex-start">
        <Typography variant="caption">
          {t('selectedPeriod', { ns: 'dashboard' })}
          :
          {' '}
        </Typography>
        <Typography fontWeight={600} variant="caption" sx={{ mb: 1 }}>
          {periodText}
        </Typography>
      </Box>
      <Box pt={4} position="fixed" bottom={-6} right={0} display="flex" gap={1} p={2} justifyContent="flex-end">
        <Button onClick={onCancel} size="small">{t('cancel')}</Button>
        <Button onClick={onAccept} variant="contained" size="small">{t('apply')}</Button>
      </Box>
    </Box>
  );
};

const StyledQuickFilter = styled(QuickFilter)({
  display: 'grid',
  alignItems: 'center',
});

const StyledToolbarButton = styled(ToolbarButton as any)<{ ownerState: OwnerState }>(({ theme, ownerState }) => ({
  gridArea: '1 / 1',
  width: 'min-content',
  height: 'min-content',
  zIndex: 1,
  opacity: ownerState.expanded ? 0 : 1,
  pointerEvents: ownerState.expanded ? 'none' : 'auto',
  transition: (theme as any)?.transitions?.create(['opacity']),
}));

const StyledTextField = styled(TextField)<{ ownerState: OwnerState }>(({ theme, ownerState }) => ({
  gridArea: '1 / 1',
  overflowX: 'clip',
  width: ownerState.expanded ? 260 : 'var(--trigger-width)',
  opacity: ownerState.expanded ? 1 : 0,
  transition: (theme as any)?.transitions?.create(['width', 'opacity']),
}));

const CustomToolbar: FC<TTableHeader> = ({
  handleExport,
  printRef,
  apiRef,
  customAction,
  setFullTable,
  isFullTable,
  isDisplayPicker,
  pickerItems,
  selectedPickerName,
  handleSetDates,
  onPickerSubmit,
  isOpenCustomPicker,
  setIsOpenCustomPicker,
  isDisplaySelectedMembers = false,
}) => {
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const exportMenuTriggerRef = useRef<HTMLButtonElement>(null);
  const [customRange, setCustomRange] = useState<[Moment | null, Moment | null]>([null, null]);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { settings } = useAppSelector(getTokenDetails);
  const { t } = useTranslation(['common', 'timesheets', 'inputs', 'payroll', 'team']);
  const theme = useTheme();
  const dispatch = useAppDispatch();

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const selectedMembers = useAppSelector(getSelectedMembers);

  const { handlePrint } = usePrint(printRef, apiRef);

  const handleFullTable = useCallback(() => {
    setFullTable(!isFullTable);
  }, [isFullTable]);

  const handleCustomDateApply = async () => {
    if (customRange[0] && customRange[1]) {
      handleSetDates?.([customRange[0], customRange[1]]);
      await onPickerSubmit?.();
    }
  };

  const handleSelectMembers = () => {
    apiRef.current.setPinnedColumns({ left: [] });
    dispatch(sheetsActions.setIsOpenEmployeesExportMode(true));
    setExportMenuOpen(false);
  };

  const handleExportSelectedMembers = () => {
    handleExport();
    dispatch(sheetsActions.setIsOpenEmployeesExportMode(false));
    dispatch(sheetsActions.setSelectedMembers([] as any));
  };

  return (
    <Toolbar style={{
      minHeight: 'auto',
    }}
    >
      <Grid pt={isMobile ? 2 : 0} container alignItems="center" display="flex" spacing={1}>
        {selectedMembers?.ids && (
          <Grid size={{
            xs: 6,
            sm: 'auto',
          }}
          >
            <Box pr={1}>
              <Button
                onClick={handleExportSelectedMembers}
                variant="outlined"
                size="small"
              >
                {t('exportMembers', { ns: 'team' })}
              </Button>
            </Box>
          </Grid>
        )}
        { customAction && (
        <Grid size={{
          xs: 6,
          sm: 'auto',
        }}
        >
          <Box pr={1}>
            {customAction}
          </Box>
        </Grid>
        )}

        {isDisplayPicker && (
        <Grid size={{
          xs: 12,
          sm: 'auto',
        }}
        >
          <BaseButtonMenu
            isRemoveBtnHover={false}
            buttonProps={{
              children: selectedPickerName,
              endIcon: <Icon path={mdiChevronDown} size={0.8} />,
              size: 'small',
              ref: buttonRef,
              variant: 'contained',
              fullWidth: isMobile,
            }}
            listProps={{
              items: pickerItems as Array<TListBaseItem>,
              sx: { width: 'auto' },
              itemfontsize: 12,
            }}
          />
        </Grid>
        )}

        {isOpenCustomPicker && (
        <Grid size={{
          xs: 12,
          sm: 'auto',
        }}
        >
          <Box pr={1} display="flex" flexDirection="column" gap={1} mt={1}>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DateRangePicker
                value={customRange}
                closeOnSelect={false}
                onChange={(newValue) => {
                  if (handleSetDates) {
                    setCustomRange(newValue);
                    handleSetDates(newValue as [Moment, Moment]);
                  }
                }}
                {...(settings.ui?.dateFormat && {
                  format: settings.ui?.dateFormat,
                })}
                open={isOpenCustomPicker}
                onClose={() => {
                  if (setIsOpenCustomPicker) {
                    setIsOpenCustomPicker(false);
                  }
                }}
                onOpen={() => {
                  if (setIsOpenCustomPicker) {
                    setIsOpenCustomPicker(true);
                  }
                }}
                calendars={2}
                localeText={{ start: t('startDate', { ns: 'timesheets' }), end: t('endDate', { ns: 'timesheets' }) }}
                slots={{
                  field: () => null,
                  ...(!isMobile && {
                    // eslint-disable-next-line react/no-unstable-nested-components
                    actionBar: (props) => <CustomDateRangeFooter {...props} selectedRange={customRange} />,
                  }),
                }}
                slotProps={{
                  popper: {
                    anchorEl: buttonRef.current,
                  },
                  actionBar: {
                    ...(isMobile && {
                      actions: ['cancel', 'accept'],
                    }),
                    onCancel: () => {
                      if (setIsOpenCustomPicker) {
                        setIsOpenCustomPicker(false);
                      }
                    },
                    onAccept: () => {
                      handleCustomDateApply();
                      if (setIsOpenCustomPicker) {
                        setIsOpenCustomPicker(false);
                      }
                    },
                  },
                }}
              />
            </LocalizationProvider>
          </Box>
        </Grid>
        )}

        {!isMobile && (
        <Grid
          ml={1}
          size={{
            xs: 6,
            sm: 'auto',
          }}
        >
          <Box pl={1}>
            <Button
              variant="contained"
              size="small"
              onClick={handleFullTable}
              fullWidth={isMobile}
              endIcon={<Iconify icon="fluent:resize-table-24-regular" width={18} sx={{ color: BASE_COLORS.white }} />}
            >
              {t('fullTable', { ns: 'orders' })}
            </Button>
          </Box>
        </Grid>
        )}

        <Grid
          {...!isMobile && {
            ml: 1,
          }}
          size={{
            xs: 4,
            sm: 'auto',
          }}
        >
          <ColumnsPanelTrigger render={(
            <ToolbarButton style={{
              ...(isMobile && {
                paddingLeft: 0,
              }),
            }}
            />
)}
          >
            <Button
              variant="contained"
              size="small"
              endIcon={<Iconify icon="flowbite:column-solid" width={16} sx={{ color: BASE_COLORS.white }} />}
            >
              {t('columns')}
            </Button>
          </ColumnsPanelTrigger>
        </Grid>

        <Grid size={{
          xs: 4,
          sm: 'auto',
        }}
        >
          <FilterPanelTrigger
            render={(props, state) => (
              <ToolbarButton
                {...props as any}
                color="default"
              >
                <Badge
                  badgeContent={state.filterCount}
                  color="primary"
                  variant="dot"
                >
                  <Button
                    variant="contained"
                    size="small"
                    fullWidth={isMobile}
                    endIcon={<Iconify icon="mingcute:filter-2-fill" width={16} sx={{ color: BASE_COLORS.white }} />}
                  >
                    {t('filters')}
                  </Button>
                </Badge>
              </ToolbarButton>
            )}
          />
        </Grid>

        <Grid size={{
          xs: 4,
          sm: 'auto',
        }}
        >
          <ToolbarButton
            ref={exportMenuTriggerRef}
            id="export-menu-trigger"
            aria-controls="export-menu"
            aria-haspopup="true"
            aria-expanded={exportMenuOpen ? 'true' : undefined}
            onClick={() => setExportMenuOpen(true)}
          >
            <Button
              variant="contained"
              size="small"
              endIcon={<Iconify icon="icon-park-outline:export" width={16} sx={{ color: BASE_COLORS.white }} />}
            >
              {t('export')}
            </Button>
          </ToolbarButton>
        </Grid>

        <Menu
          id="export-menu"
          anchorEl={exportMenuTriggerRef.current}
          open={exportMenuOpen}
          onClose={() => setExportMenuOpen(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          slotProps={{
            list: { 'aria-labelledby': 'export-menu-trigger' },
          }}
        >
          <ExportPrint render={<MenuItem />} onClick={() => { handlePrint(); setExportMenuOpen(false); }}>
            {t('print')}
          </ExportPrint>
          <ExportCsv render={<MenuItem onClick={handleExport} />} onClick={() => setExportMenuOpen(false)}>
            {t('downloadCsv')}
          </ExportCsv>
          {isDisplaySelectedMembers && (
          <MenuItem onClick={handleSelectMembers}>
            {t('selectMembers', { ns: 'payroll' })}
          </MenuItem>
          )}
        </Menu>

        <Grid size={{
          xs: 12,
          sm: 'auto',
        }}
        >
          <StyledQuickFilter>
            <QuickFilterTrigger
              render={(triggerProps, state) => (
                <StyledToolbarButton
                  {...triggerProps}
                  ownerState={{ expanded: state.expanded }}
                  color="default"
                  aria-disabled={state.expanded}
                  style={{
                    ...(isMobile && {
                      width: '100%',
                      paddingLeft: 0,
                      paddingRight: 0,
                    }),
                  }}
                >
                  <Button
                    variant="contained"
                    disableRipple
                    size="small"
                    fullWidth={isMobile}
                    endIcon={<Iconify icon="ic:outline-search" width={18} sx={{ color: BASE_COLORS.white }} />}
                  >
                    {t('search', { ns: 'inputs' })}
                  </Button>
                </StyledToolbarButton>
              )}
            />
            <QuickFilterControl
              render={({ ref, ...controlProps }, state) => (
                <StyledTextField
                  {...controlProps}
                  ownerState={{ expanded: state.expanded }}
                  inputRef={ref}
                  aria-label="Search"
                  fullWidth={isMobile}
                  placeholder={`${t('search', { ns: 'inputs' })}...`}
                  size="small"
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                      endAdornment: state.value ? (
                        <InputAdornment position="end">
                          <QuickFilterClear edge="end" size="small" aria-label="Clear search">
                            <CancelIcon fontSize="small" />
                          </QuickFilterClear>
                        </InputAdornment>
                      ) : null,
                      ...controlProps.slotProps?.input,
                    },
                    ...controlProps.slotProps,
                  }}
                />
              )}
            />
          </StyledQuickFilter>
        </Grid>
      </Grid>
    </Toolbar>
  );
};

export { CustomToolbar };
